import {
  BankIcon,
  CalculatorIcon,
  ConfirmIcon,
  DateIcon,
  DollarIcon,
  ListIcon,
  NoteIcon,
  HashTagIcon,
  ThunderIcon,
  TreeDiagramIcon,
} from 'app/shared/componentsRestyled/coreDesignSystem/icons';

export const FORM_FIELD_TYPE = {
  TEXT: 'Text',
  NUMBER: 'Number',
  CURRENCY: 'Currency',
  DATE: 'Date',
  ENUM: 'Enum',
  BOOLEAN: 'Boolean',
  REPEATING_TABLE: 'RepeatingTable',
};

export const FORM_FIELD_VALUE_SOURCE = {
  AI_FILLED: 'AiFilled',
  RECORD_SOURCED: 'RecordSourced',
  DERIVED: 'Derived',
};

export const DEFAULT_FORM_FIELD_VALUE_SOURCE = FORM_FIELD_VALUE_SOURCE.AI_FILLED;

const FORM_FIELD_VALUE_SOURCE_DEFINITIONS = {
  [FORM_FIELD_VALUE_SOURCE.AI_FILLED]: {
    value: FORM_FIELD_VALUE_SOURCE.AI_FILLED,
    label: 'AI Filled',
    shortLabel: 'AI',
    description: 'The AI fills this value from the documents and context it reviews.',
    icon: ThunderIcon,
  },
  [FORM_FIELD_VALUE_SOURCE.RECORD_SOURCED]: {
    value: FORM_FIELD_VALUE_SOURCE.RECORD_SOURCED,
    label: 'Record Sourced',
    shortLabel: 'Record',
    description: 'The value is read from the existing loan record, not generated by the AI.',
    icon: BankIcon,
  },
  [FORM_FIELD_VALUE_SOURCE.DERIVED]: {
    value: FORM_FIELD_VALUE_SOURCE.DERIVED,
    label: 'Derived',
    shortLabel: 'Derived',
    description: 'The value is calculated from other fields rather than entered directly.',
    icon: CalculatorIcon,
  },
};

export const FORM_FIELD_VALUE_SOURCE_OPTIONS = Object.values(
  FORM_FIELD_VALUE_SOURCE_DEFINITIONS,
).map(({ value, label }) => ({ value, name: label }));

export const FORM_FIELD_VALUE_SOURCE_LEGEND = Object.values(FORM_FIELD_VALUE_SOURCE_DEFINITIONS);

export const getFormFieldValueSourceDefinition = valueSource =>
  FORM_FIELD_VALUE_SOURCE_DEFINITIONS[valueSource] ?? null;

export const normalizeValueSource = valueSource =>
  getFormFieldValueSourceDefinition(valueSource) ? valueSource : DEFAULT_FORM_FIELD_VALUE_SOURCE;

const FORM_FIELD_TYPE_DEFINITIONS = {
  [FORM_FIELD_TYPE.TEXT]: {
    value: FORM_FIELD_TYPE.TEXT,
    label: 'Text',
    hasAllowedValues: false,
    hasItemFields: false,
    icon: NoteIcon,
  },
  [FORM_FIELD_TYPE.NUMBER]: {
    value: FORM_FIELD_TYPE.NUMBER,
    label: 'Number',
    hasAllowedValues: false,
    hasItemFields: false,
    icon: HashTagIcon,
  },
  [FORM_FIELD_TYPE.CURRENCY]: {
    value: FORM_FIELD_TYPE.CURRENCY,
    label: 'Currency',
    hasAllowedValues: false,
    hasItemFields: false,
    icon: DollarIcon,
  },
  [FORM_FIELD_TYPE.DATE]: {
    value: FORM_FIELD_TYPE.DATE,
    label: 'Date',
    hasAllowedValues: false,
    hasItemFields: false,
    icon: DateIcon,
  },
  [FORM_FIELD_TYPE.ENUM]: {
    value: FORM_FIELD_TYPE.ENUM,
    label: 'Enum',
    hasAllowedValues: true,
    hasItemFields: false,
    icon: ListIcon,
  },
  [FORM_FIELD_TYPE.BOOLEAN]: {
    value: FORM_FIELD_TYPE.BOOLEAN,
    label: 'Boolean',
    hasAllowedValues: false,
    hasItemFields: false,
    icon: ConfirmIcon,
  },
  [FORM_FIELD_TYPE.REPEATING_TABLE]: {
    value: FORM_FIELD_TYPE.REPEATING_TABLE,
    label: 'Repeating Table',
    hasAllowedValues: false,
    hasItemFields: true,
    icon: TreeDiagramIcon,
  },
};

export const FORM_FIELD_TYPE_OPTIONS = Object.values(FORM_FIELD_TYPE_DEFINITIONS).map(
  ({ value, label, icon }) => ({ value, name: label, icon }),
);

export const getFormFieldTypeDefinition = type => FORM_FIELD_TYPE_DEFINITIONS[type] ?? null;

export const typeHasAllowedValues = type => !!getFormFieldTypeDefinition(type)?.hasAllowedValues;

export const typeHasItemFields = type => !!getFormFieldTypeDefinition(type)?.hasItemFields;
